// ============================================================================
// Kassaning lokal bazasi (IndexedDB). Internet bo'lmasa ham kassa shu yerdan
// ishlaydi: menyu keshi, navbatdagi amallar (outbox), shu kassadagi buyurtmalar.
//
//   meta    — kalit/qiymat: katalog, smena, PIN kalitlari, lokal raqamlar
//   outbox  — serverga yuborilishi kerak bo'lgan amallar (tartib bilan)
//   orders  — shu kassada qilingan buyurtmalar (client_id bo'yicha)
// ============================================================================

const DB_NAME = 'onepos-kassa';
const DB_VERSION = 1;

let dbPromise: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('meta')) db.createObjectStore('meta');
      if (!db.objectStoreNames.contains('outbox')) db.createObjectStore('outbox', { keyPath: 'id', autoIncrement: true });
      if (!db.objectStoreNames.contains('orders')) db.createObjectStore('orders', { keyPath: 'client_id' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => {
      dbPromise = null;
      reject(req.error ?? new Error('IndexedDB ochilmadi'));
    };
  });
  return dbPromise;
}

type StoreName = 'meta' | 'outbox' | 'orders';

async function tx<T>(store: StoreName, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await open();
  return new Promise<T>((resolve, reject) => {
    const t = db.transaction(store, mode);
    const req = fn(t.objectStore(store));
    t.oncomplete = () => resolve(req.result);
    t.onerror = () => reject(t.error ?? req.error ?? new Error('IndexedDB xatosi'));
    t.onabort = () => reject(t.error ?? new Error('IndexedDB bekor qilindi'));
  });
}

export const idb = {
  get<T>(store: StoreName, key: IDBValidKey): Promise<T | undefined> {
    return tx<T | undefined>(store, 'readonly', (s) => s.get(key) as IDBRequest<T | undefined>);
  },
  put(store: StoreName, value: unknown, key?: IDBValidKey): Promise<IDBValidKey> {
    return tx(store, 'readwrite', (s) => (key === undefined ? s.put(value) : s.put(value, key)));
  },
  del(store: StoreName, key: IDBValidKey): Promise<undefined> {
    return tx(store, 'readwrite', (s) => s.delete(key));
  },
  all<T>(store: StoreName): Promise<T[]> {
    return tx<T[]>(store, 'readonly', (s) => s.getAll() as IDBRequest<T[]>);
  },
  count(store: StoreName): Promise<number> {
    return tx(store, 'readonly', (s) => s.count());
  },
  clear(store: StoreName): Promise<undefined> {
    return tx(store, 'readwrite', (s) => s.clear());
  },
};

export const meta = {
  get: <T>(key: string) => idb.get<T>('meta', key),
  set: (key: string, value: unknown) => idb.put('meta', value, key),
  del: (key: string) => idb.del('meta', key),
};
